import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { toast } from 'react-toastify';

import { AccountingApi, MainApi } from '_api';
import { FILE_TYPES } from '_constants/fileTypes';
import { FIELD_KEYS } from '_constants/forms';
import { createBody } from '_helpers/amendment';
import { handleError, handleResponse } from '_helpers/api';
import { selectForm, selectProductIdByFileId } from '_store/amendment/selector';
import { selectCategoryByProductId } from '_store/order/selector';

export const getAllTaxReturns = createAsyncThunk(
  'amendment/getAllTaxReturns',
  async function ({ customerId, productIds = [] }, { rejectWithValue }) {
    try {
      const getTaxReturns = async (productId) => {
        const response = await AccountingApi.getStatements({
          customerId,
          productId,
          type: FILE_TYPES.previousYearTaxReturn,
          page: 1,
          count: 200,
        });

        const rawResult = handleResponse(response);

        return rawResult?.elements?.map(({ id, type, year, description }) => {
          const uid = `${productId}${id}`;
          return { uid, productId, id, type, year, description };
        });
      };

      const results = await Promise.all(productIds.map((productId) => getTaxReturns(productId)));

      return results.reduce((acc, result) => {
        return [...acc, ...result];
      }, []);
    } catch (e) {
      return rejectWithValue(handleError(e));
    }
  }
);

export const createAmendment = createAsyncThunk(
  'amendment/createAmendment',
  async function ({ orderId }, { dispatch, getState, rejectWithValue }) {
    try {
      const form = selectForm(getState());
      const productId = selectProductIdByFileId(form[FIELD_KEYS.returnToAmend])(getState());
      const category = selectCategoryByProductId(productId)(getState());
      const body = createBody({ category, form });
      await MainApi.createDelayedUpsell({ orderId, body });
      dispatch(resetForm());
      toast.success('Amendment requested successfully!');
    } catch (e) {
      const parsedError = handleError(e);
      toast.error(parsedError?.message || 'Something went wrong');
      return rejectWithValue(parsedError);
    }
  }
);

const initialForm = {
  [FIELD_KEYS.returnToAmend]: '',
  [FIELD_KEYS.reasonForAmendment]: '',
};

const amendmentSlice = createSlice({
  name: 'amendment',
  initialState: {
    form: {
      ...initialForm,
    },
    data: [],
    error: null,
    loading: false,
    creating: false,
  },
  reducers: {
    refreshForm(state, action) {
      state.form = {
        ...state.form,
        ...action.payload,
      };
    },
    resetForm(state) {
      state.form = {
        ...initialForm,
      };
    },
    resetData(state) {
      state.data = [];
    },
    resetError(state) {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getAllTaxReturns.pending, (state) => {
        state.error = null;
        state.loading = true;
      })
      .addCase(getAllTaxReturns.fulfilled, (state, action) => {
        state.data = action.payload;
        state.loading = false;
      })
      .addCase(getAllTaxReturns.rejected, (state, action) => {
        state.error = action.payload;
        state.loading = false;
      });

    builder
      .addCase(createAmendment.pending, (state) => {
        state.error = null;
        state.creating = true;
      })
      .addCase(createAmendment.fulfilled, (state) => {
        state.creating = false;
      })
      .addCase(createAmendment.rejected, (state, action) => {
        state.error = action.payload;
        state.creating = false;
      });
  },
});

export const { refreshForm, resetForm, resetData, resetError } = amendmentSlice.actions;

export default amendmentSlice.reducer;
